import React, { useId, useState } from 'react';
import { EvidenceItem } from '../../utils/types';
import { EVIDENCE_PREVIEW_ROWS, EvidenceRow, buildEvidence, quoteSource } from './evidence';

interface EvidenceChipsProps {
  items: EvidenceItem[];
  onOpenEvidence?: (ref: { section: string; filter?: string }) => void;
}

/**
 * An issue card's evidence (DESIGN.md "Evidence as a summary and a ruled
 * list"): policy excerpts as quotes, everything else as one ruled row per
 * item with its meta in plain words. A long list shows its first
 * EVIDENCE_PREVIEW_ROWS rows and a "Show all" button; print shows every row.
 */
export default function EvidenceChips({ items, onOpenEvidence }: EvidenceChipsProps) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  if (!items || items.length === 0) return null;

  const { quotes, rows, sharedNotes, summary, collapsible } = buildEvidence(items);
  const collapsed = collapsible && !expanded;

  return (
    <div className="evidence">
      {quotes.map((q, idx) => (
        <figure key={`q${idx}`} className="evidence-quote">
          <blockquote className="evidence-quote__text">{'“' + q.value + '”'}</blockquote>
          <figcaption className="evidence-quote__source">{quoteSource(q.label)}</figcaption>
        </figure>
      ))}

      {summary && <p className="evidence__summary">{summary}</p>}

      {rows.length > 0 && (
        <ul id={listId} className={`evidence-list${collapsed ? ' evidence-list--collapsed' : ''}`} aria-label="Evidence">
          {rows.map((row, idx) => (
            <EvidenceRowView
              key={idx}
              row={row}
              extra={collapsible && idx >= EVIDENCE_PREVIEW_ROWS}
              onOpenEvidence={onOpenEvidence}
            />
          ))}
        </ul>
      )}

      {sharedNotes.map((note) => (
        <p key={note} className="evidence__shared">
          {note}
        </p>
      ))}

      {collapsible && (
        <button
          type="button"
          className="evidence__more no-print"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

function EvidenceRowView({
  row,
  extra,
  onOpenEvidence,
}: {
  row: EvidenceRow;
  extra: boolean;
  onOpenEvidence?: (ref: { section: string; filter?: string }) => void;
}) {
  const appendixRef = onOpenEvidence ? row.item.appendixRef : undefined;
  const className = [
    'evidence-row',
    row.continued ? 'evidence-row--continued' : '',
    extra ? 'evidence-row--extra' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={className}>
      <div className="evidence-row__what">
        <span className={`evidence-row__title${row.titleIsCode ? ' evidence-row__title--code' : ''}`}>
          {/* A vendor's second host: the name stays for screen readers, not for the eye. */}
          {row.continued ? <span className="sr-only">{row.title}</span> : row.title}
        </span>
        {row.location && (
          <span className="evidence-row__location" title={row.item.value}>
            {row.location}
          </span>
        )}
      </div>
      {row.notes.length > 0 && <div className="evidence-row__notes">{row.notes.join(' · ')}</div>}
      {appendixRef && onOpenEvidence && (
        <button
          type="button"
          className="evidence-row__open"
          aria-label={`View in appendix: ${row.title}`}
          title="View in appendix"
          onClick={() => onOpenEvidence(appendixRef)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </li>
  );
}
